import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Chip,
  Paper,
  CircularProgress,
} from '@mui/material';
import { Link } from 'react-router-dom';
import { apiFetch } from '../utils/api';

const WhatToEatToday = () => {
  const [foodTypes, setFoodTypes] = useState([]);
  const [currentFoodType, setCurrentFoodType] = useState('');
  const [peopleNumber, setPeopleNumber] = useState(1);
  const [suggestions, setSuggestions] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMoreResults, setHasMoreResults] = useState(true);
  const [loading, setLoading] = useState(false);

  const handleAddFoodType = (e) => {
    if (e.key === 'Enter' && currentFoodType.trim() !== '') {
      setFoodTypes((prev) => [...prev, currentFoodType.trim()]);
      setCurrentFoodType('');
    }
  };

  const handleDeleteFoodType = (typeToDelete) => {
    setFoodTypes((prev) => prev.filter((type) => type !== typeToDelete));
  };

  const handleSearch = async (resetPage = false) => {
    if (resetPage) {
      setPage(1);
      setSuggestions([]);
    }

    try {
      setLoading(true);
      const queryParams = new URLSearchParams({
        foodTypes: foodTypes.join(','),
        peopleNumber,
        page: resetPage ? 1 : page,
        limit: 15
      });

      const response = await apiFetch(`/api/recipes/suggestions?${queryParams.toString()}`);

      if (!response.ok) {
        throw new Error('Failed to fetch suggestions');
      }

      const result = await response.json();

      setSuggestions(prev => resetPage ? result.recipes : [...prev, ...result.recipes]);
      setHasMoreResults(result.totalRecipes > (resetPage ? 1 : page) * 15);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      alert('Hubo un error al obtener las sugerencias.');
      setLoading(false);
    }
  };

  const handleLoadMore = () => {
    setPage(prevPage => prevPage + 1);
  };

  useEffect(() => {
    handleSearch();
  }, [page]); // Trigger the search whenever page changes

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="h5" gutterBottom>
          ¿Qué te apetece comer hoy?
        </Typography>
        <TextField
          fullWidth
          label="Tipo de comida (pulsa Enter para añadir más)"
          value={currentFoodType}
          onChange={(e) => setCurrentFoodType(e.target.value)}
          onKeyDown={handleAddFoodType}
          variant="outlined"
          sx={{ mb: 2 }}
        />
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {foodTypes.map((type, index) => (
            <Chip
              key={index}
              label={type}
              onDelete={() => handleDeleteFoodType(type)}
            />
          ))}
        </Box>
        <TextField
          fullWidth
          label="Número de personas"
          type="number"
          value={peopleNumber}
          onChange={(e) => setPeopleNumber(e.target.value)}
          variant="outlined"
          InputProps={{ inputProps: { min: 1 } }}
          sx={{ mb: 2 }}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => handleSearch(true)} // Resets page to 1
          sx={{ mt: 2 }}
        >
          Buscar Recetas
        </Button>
      </Paper>

      {suggestions.length > 0 && (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Sugerencias de recetas:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {suggestions.map((recipe) => (
              <Box
                key={recipe._id}
                component={Link}
                to={`/recipes/${recipe._id}`}
                sx={{
                  p: 2,
                  border: '1px solid #ddd',
                  textDecoration: 'none',
                  color: 'inherit',
                  '&:hover': {
                    backgroundColor: '#f0f0f0',
                  },
                }}
              >
                <Typography variant="h6">{recipe.title}</Typography>
                <Typography variant="body2">
                  Para {recipe.peopleNumber} personas
                </Typography>
              </Box>
            ))}
          </Box>
          {hasMoreResults && !loading && (
            <Button variant="contained" onClick={handleLoadMore} sx={{ display: 'block', margin: '20px auto' }}>
              Cargar más
            </Button>
          )}
          {loading && (
            <Box sx={{ textAlign: 'center', margin: '20px auto' }}>
              <CircularProgress />
            </Box>
          )}
        </Paper>
      )}
    </Box>
  );
};

export default WhatToEatToday;
